// src/pages/EditZone.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const baseUrl = config.baseURL;

const LOCATION_OPTIONS = [
  "sidebar",
  "navbar",
  "main_top",
  "main_bottom",
  "footer",
  "custom",
];

const modulesList = [
  "dashboard",
  "customer",
  "categories",
  "complaint",
  "earnings",
  "expenses",
  "faqs",
  "invoice",
  "materials",
  "orders",
  "pages",
  "ProductionBatch",
  "products",
  "rewardPoints",
  "ShippingCompany",
  "suppliers",
  "rewardsettings",
  "pricing",
  "edit profile",
  "change password",
  "logout",
];

const EditZone = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const token = localStorage.getItem("token");

  const [form, setForm] = useState({
    name: "",
    description: "",
    width: "",
    height: "",
    max_ads: 1,
    location: "main_top",
    page_name: ["dashboard"],
    order_index: 0,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchZone = async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/zone/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = res.data;
        setForm({
          name: data.name || "",
          description: data.description || "",
          width: data.width || "",
          height: data.height || "",
          max_ads: data.max_ads || 1,
          location: data.location || "main_top",
          page_name: data.page_name || ["dashboard"],
          order_index: data.order_index || 0,
        });
      } catch (err) {
        console.error("Failed to fetch zone:", err);
        setError(err.response?.data?.error || "Failed to fetch zone data.");
      } finally {
        setLoading(false);
      }
    };

    fetchZone();
    // eslint-disable-next-line
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, page_name: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.location) {
      setError("Name and Location are required.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const res = await axios.put(`${baseUrl}/api/zone/${id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("res", res);
      console.log("id", id);
      console.log("form", form);
      if (res) {
        toast.success("Zone updated successfully!");
        navigate("/dashboard/zones");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to update zone.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", mt: 6, px: 2 }}>
      <Typography
        variant="h4"
        mb={4}
        sx={{ fontWeight: "bold", textAlign: "center", color: "#2E4053" }}
      >
        Edit Zone
      </Typography>
      <ToastContainer position="top-right" autoClose={5000} />

      <form onSubmit={handleSubmit}>
        <Paper
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 4,
            boxShadow: 4,
            backgroundColor: "#f9f9f9",
            "&:hover": { boxShadow: 6 },
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Zone Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Width in pixel"
                name="width"
                type="number"
                value={form.width}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Height in pixel"
                name="height"
                type="number"
                value={form.height}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Max Ads"
                name="max_ads"
                type="number"
                value={form.max_ads}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required disabled>
                <InputLabel>Location</InputLabel>
                <Select
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                >
                  {LOCATION_OPTIONS.map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {loc.replace("_", " ").toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Page Name</InputLabel>
                <Select
                  multiple
                  name="page_name"
                  value={form.page_name}
                  onChange={handlePageChange}
                >
                  {modulesList.map((mod) => (
                    <MenuItem key={mod} value={mod}>
                      {mod.charAt(0).toUpperCase() + mod.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Order Index"
                name="order_index"
                type="number"
                value={form.order_index}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mt: 2,
            mb: 4,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: "#2E4053",
              "&:hover": { backgroundColor: "#c7570f" },
              fontWeight: "bold",
              textTransform: "none",
              px: 4,
              py: 1.5,
              borderRadius: 3,
            }}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : "Update Zone"}
          </Button>
          <Button
            variant="outlined"
            sx={{
              borderColor: "#2E4053",
              color: "#2E4053",
              "&:hover": { borderColor: "#c7570f", color: "#c7570f" },
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: "bold",
              textTransform: "none",
            }}
            onClick={() => navigate("/dashboard/zones")}
          >
            Cancel
          </Button>
        </Box>

        {error && (
          <Typography
            color="error"
            sx={{ mt: 2, textAlign: "center", fontWeight: "bold" }}
          >
            {error}
          </Typography>
        )}
      </form>
    </Box>
  );
};

export default EditZone;
